import { RawTrade, Trade, TradeDirection } from './types';
import { getTradeDirectionFromTradeSide } from './tradeSide';
import convertTimestampToTime from '../time/convertTimestampToTime';
import { calculateHeatScore, MAX_HEAT_SCORE } from './scores';
import getExpiryString from './getExpiryString';
import getDaysToExpiry from './getDaysToExpiry';
import isUnusual from './isUnusual';
import isTopPosition from './isTopPosition';
import isGoldenSweep from './isGoldenSweep';
import { getOptionFlowTimestampMs } from 'utils/shared/trades/getOptionFlowTimestampMs';

const SCORE_LIFT = 4;
const TRADE_DIRECTION_DEEP = 1.005;

const getDerivedTradeValues = (rawTrade: RawTrade, isWallstreetTimezone: boolean): Trade => {
  const tradeTimestamp = getOptionFlowTimestampMs(rawTrade);
  const trade: Trade = {
    ...rawTrade,
    daysToExpiry: getDaysToExpiry(rawTrade),
    expiryString: getExpiryString(rawTrade),
    createdTimestamp: new Date(rawTrade.createdAt).getTime(),
    tradeTimestamp,
    heatScore: 0,
    scoreWidth: 0,
    time: '',
    timeSubscript: '',
    isUnusual: false,
    isTopPosition: false,
    isGoldenSweep: false,
  };

  const { time, timeSubscript } =
    tradeTimestamp === undefined
      ? { time: '', timeSubscript: '' }
      : convertTimestampToTime({ timestamp: tradeTimestamp, isWallstreetTimezone });

  const heatScore = calculateHeatScore(trade) || 0;
  const scoreWidth = Math.min((heatScore / (MAX_HEAT_SCORE - SCORE_LIFT)) * 100, 100);
  const rawTradeSideDirection = getTradeDirectionFromTradeSide(rawTrade.tradeSide);
  let tradeDirection: TradeDirection | undefined;

  if (rawTradeSideDirection === TradeDirection.Mid) {
    tradeDirection = TradeDirection.Mid;
  } else if (rawTradeSideDirection === TradeDirection.AboveAsk) {
    tradeDirection = TradeDirection.AboveAsk;
  } else if (rawTradeSideDirection === TradeDirection.BelowBid) {
    tradeDirection = TradeDirection.BelowBid;
  } else if (rawTradeSideDirection === TradeDirection.Ask) {
    tradeDirection = TradeDirection.Ask;

    if (rawTrade.price && rawTrade.nbboAsk) {
      if (rawTrade.price >= rawTrade.nbboAsk * TRADE_DIRECTION_DEEP) {
        tradeDirection = TradeDirection.AboveAsk;
      }
    }
  } else if (rawTradeSideDirection === TradeDirection.Bid) {
    tradeDirection = TradeDirection.Bid;

    if (rawTrade.price && rawTrade.nbboBid) {
      if (rawTrade.price <= rawTrade.nbboBid / TRADE_DIRECTION_DEEP) {
        tradeDirection = TradeDirection.BelowBid;
      }
    }
  }

  return {
    ...trade,
    heatScore,
    scoreWidth,
    time,
    timeSubscript,
    isUnusual: isUnusual(trade),
    isTopPosition: isTopPosition(trade),
    isGoldenSweep: isGoldenSweep(trade),
    tradeDirection,
  };
};

export default getDerivedTradeValues;
